import { APP_V2_APP_EVENTS, logError } from "../../lib/analytics";

export type CognitoAuthFlow = "magic-link" | "password";

export function mapCognitoError(
  error: unknown,
  flow: CognitoAuthFlow = "magic-link"
): { code: string; message: string } {
  if (error && typeof error === "object" && "name" in error) {
    const amplifyError = error as { name: string; message: string };

    switch (amplifyError.name) {
      // When the DefineAuthChallenge Lambda throws an error (instead of returning
      // failAuthentication: true), Cognito surfaces it as a UserLambdaValidationException
      // with the Lambda's error message embedded. We use this to distinguish "user type
      // not allowed" from generic auth failures.
      case "UserLambdaValidationException": {
        if (amplifyError.message.includes("USER_TYPE_NOT_ALLOWED")) {
          return {
            code: "auth/user-type-not-allowed",
            message:
              "Your account type does not have access to this application. Please make sure you are using the correct app for your account.",
          };
        }
        logError(APP_V2_APP_EVENTS.COGNITO_AUTH_UNEXPECTED_ERROR, {
          error,
          metadata: { errorName: amplifyError.name, errorMessage: amplifyError.message },
        });
        return {
          code: "auth/lambda-error",
          message: "An authentication error occurred.",
        };
      }
      case "UserNotFoundException": {
        return {
          code: "auth/user-not-found",
          message: "No registered user found for the provided email address.",
        };
      }
      // Cognito returns NotAuthorizedException with "Incorrect username or password" for:
      // - Magic-link flow: challenge response failed → expired/invalid link
      // - Password flow: wrong credentials entered by user
      case "NotAuthorizedException": {
        if (amplifyError.message.includes("Incorrect username or password")) {
          if (flow === "password") {
            return {
              code: "auth/incorrect-credentials",
              message: "Incorrect email or password. Please try again.",
            };
          }
          return {
            code: "auth/invalid-link",
            message:
              "This sign-in link has expired or has already been used. Links are valid for 10 minutes. Please request a new one.",
          };
        }
        return {
          code: "auth/user-not-found",
          message: "No registered user found for the provided email address.",
        };
      }
      case "TooManyRequestsException": {
        return {
          code: "auth/too-many-requests",
          message: "Too many requests, please try again later",
        };
      }
      case "LimitExceededException": {
        return {
          code: "auth/limit-exceeded",
          message: "Attempt limit exceeded, please try after some time",
        };
      }
      case "CodeMismatchException": {
        return {
          code: "auth/invalid-code",
          message: "The reset code is invalid. Please request a new one.",
        };
      }
      case "ExpiredCodeException": {
        return {
          code: "auth/expired-code",
          message: "The reset code has expired. Please request a new one.",
        };
      }
      case "PasswordResetRequiredException": {
        if (flow === "password") {
          return {
            code: "auth/password-reset-required",
            message:
              'Your password needs to be reset. Please click "Forgot password?" below to set a new password.',
          };
        }
        return {
          code: "auth/password-reset-required",
          message:
            "Your password needs to be reset before you can sign in. Please go back to the login page and use the password login option to reset your password.",
        };
      }
      case "NetworkError": {
        return { code: "auth/network-request-failed", message: "Network error occurred" };
      }
      default: {
        logError(APP_V2_APP_EVENTS.COGNITO_AUTH_UNEXPECTED_ERROR, {
          error,
          metadata: { errorName: amplifyError.name, errorMessage: amplifyError.message },
        });
        return {
          code: "auth/unknown",
          message: amplifyError.message || "An unknown error occurred",
        };
      }
    }
  }

  logError(APP_V2_APP_EVENTS.COGNITO_AUTH_UNEXPECTED_ERROR, {
    error: error instanceof Error ? error : new Error(String(error)),
    metadata: { errorMessage: String(error) },
  });
  return { code: "auth/unknown", message: "An unknown error occurred" };
}
